/* =============================================================================
   Helium AI — render primitives (the block components)
   Ported pixel-for-pixel from the Higgins design (he-primitives.jsx), typed
   against the shared catalog prop types. The agent emits structured blocks
   (via `render_*` tools); these render them natively in the Helium design
   system. <EveAssistant> dispatches each block to the matching primitive here.
   ========================================================================== */
import { Fragment, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { PageRefreshFooter, usePageRefresh } from './pageRefresh';
import { usePageVisibility, VisibilityControl } from './pageVisibility';
import {
  areaDims,
  areaGeometry,
  areaLabelsOf,
  barFraction,
  categoricalColor,
  formatTick,
  mapDisplayLegend,
  mapPointColor,
  pieGeometry,
  PIE_DIMS,
  radialGeometry,
  splitColor,
  splitPieces,
  sparseXTicks,
  tickUnit,
  xTickAnchor,
  type ChildBlock,
  type ComponentProps,
  type MapPoint,
  type MetricItem,
} from '@catalog';
import { useChatActions } from './ChatActions';
import { getMapboxToken } from '../lib/mapboxToken';
import { useTheme } from '../lib/theme';
// Type-only (erased at build) so the runtime `import('mapbox-gl')` stays lazy.
import type { Map as MapboxMap } from 'mapbox-gl';

// ---- inline link: shared by prose (RichText) and table link cells --------
const INLINE_LINK_STYLE: CSSProperties = { color: 'var(--he-purple)', fontWeight: 600, textDecoration: 'none' };

/** An http(s) anchor, or plain text when the href is unsafe. The single anchor impl. */
export function InlineLink({ href, children }: { href: string; children: ReactNode }) {
  const safe = safeHref(href);
  if (!safe) return <>{children}</>;
  return (
    <a href={safe} target="_blank" rel="noopener noreferrer" style={INLINE_LINK_STYLE}>
      {children}
    </a>
  );
}

// ---- inline parser: **bold**, [text](url) links, and bare http(s) URLs ----
// One capturing group so String.split keeps the tokens interleaved with text.
const INLINE_TOKEN = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)\s]+\)|https?:\/\/[^\s<]+)/g;
const MD_LINK = /^\[([^\]]+)\]\(([^)\s]+)\)$/;

/**
 * A bare-URL token greedily grabs trailing prose punctuation ("see https://x.com.")
 * and an enclosing paren's close ("(https://x.com)"). Peel those off the href and
 * return them as following text. The ")" is only stripped when it is unbalanced
 * (more ")" than "(" in the URL), so a URL that legitimately ends in ")" survives.
 */
function trimUrlTail(token: string): { url: string; tail: string } {
  let url = token;
  let tail = '';
  for (;;) {
    if (/[.,;:!?]$/.test(url)) {
      tail = url.slice(-1) + tail;
      url = url.slice(0, -1);
    } else if (url.endsWith(')') && url.split(')').length > url.split('(').length) {
      tail = `)${tail}`;
      url = url.slice(0, -1);
    } else {
      return { url, tail };
    }
  }
}

export function RichText({
  text,
  className,
  style,
}: {
  text: string;
  className?: string;
  style?: CSSProperties;
}) {
  const parts = String(text).split(INLINE_TOKEN);
  return (
    <p className={className} style={style}>
      {parts.map((p, i) => {
        if (!p) return null;
        if (p.startsWith('**') && p.endsWith('**')) {
          return (
            <strong key={i} style={{ fontWeight: 600, color: 'var(--fg-1)' }}>
              {p.slice(2, -2)}
            </strong>
          );
        }
        const md = p.match(MD_LINK);
        if (md) {
          return (
            <InlineLink key={i} href={md[2]}>
              {md[1]}
            </InlineLink>
          );
        }
        if (/^https?:\/\//.test(p)) {
          // Keep trailing prose punctuation / an enclosing ")" out of the href.
          const { url, tail } = trimUrlTail(p);
          return (
            <Fragment key={i}>
              <InlineLink href={url}>{url}</InlineLink>
              {tail}
            </Fragment>
          );
        }
        return <span key={i}>{p}</span>;
      })}
    </p>
  );
}

// ---- AI sparkle mark -----------------------------------------------------
export function Sparkle({ size = 18, color = '#fff' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M12 2.5l1.6 5.2a3 3 0 0 0 2 2L20.8 11.3l-5.2 1.6a3 3 0 0 0-2 2L12 20.1l-1.6-5.2a3 3 0 0 0-2-2L3.2 11.3l5.2-1.6a3 3 0 0 0 2-2L12 2.5z"
        fill={color}
      />
      <circle cx="19" cy="4.5" r="1.4" fill={color} opacity="0.8" />
      <circle cx="5" cy="18" r="1.1" fill={color} opacity="0.7" />
    </svg>
  );
}

// A translucent tint of a color token — one semantic OKLCH palette (see
// colors_and_type.css) drives every soft fill via color-mix instead of
// hand-written rgba() literals.
export const tint = (color: string, pct: number) => `color-mix(in srgb, ${color} ${pct}%, transparent)`;

/** Semantic tone tokens, shared by status badges, the Badge block, and callouts. */
export const SEMANTIC_TOKEN = {
  success: 'var(--he-success)',
  danger: 'var(--he-danger)',
  warning: 'var(--he-warning)',
  info: 'var(--he-info)',
  neutral: 'var(--he-neutral)',
} as const;

// ---- status badge --------------------------------------------------------
// Health maps to the semantic tokens: online→success, degraded→warning, offline→neutral.
const STATUS_TONE: Record<string, { token: string; ring?: boolean }> = {
  online: { token: SEMANTIC_TOKEN.success, ring: true },
  degraded: { token: SEMANTIC_TOKEN.warning },
  offline: { token: SEMANTIC_TOKEN.neutral },
};
export function StatusBadge({ status }: { status: string }) {
  const t = STATUS_TONE[status] || STATUS_TONE.offline;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 10px 4px 8px',
        borderRadius: 999,
        background: tint(t.token, 12),
        color: t.token,
        fontSize: 13,
        fontWeight: 600,
        textTransform: 'capitalize',
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: 999,
          background: t.token,
          boxShadow: t.ring ? `0 0 0 3px ${tint(t.token, 20)}` : 'none',
        }}
      />
      {status}
    </span>
  );
}

// ---- Metric KPI card -----------------------------------------------------
function Metric({ item }: { item: MetricItem }) {
  const dir = item.dir;
  const chColor =
    dir === 'up' ? SEMANTIC_TOKEN.success : dir === 'down' ? SEMANTIC_TOKEN.danger : 'var(--fg-2)';
  return (
    <div
      className="he-lift"
      style={{
        background: 'var(--bg-1)',
        borderRadius: 16,
        padding: '16px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        minWidth: 0,
      }}
    >
      <div
        style={{
          fontSize: 13,
          color: 'var(--fg-2)',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {item.label}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap' }}>
        {item.prefix && <span style={{ fontSize: 18, fontWeight: 600 }}>{item.prefix}</span>}
        <span style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1 }}>
          {item.value}
        </span>
        {item.suffix && (
          <span style={{ fontSize: 13, color: 'var(--fg-2)', fontWeight: 600 }}>{item.suffix}</span>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 18 }}>
        {item.change && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
              fontSize: 13,
              fontWeight: 600,
              color: chColor,
            }}
          >
            {dir === 'up' ? '▲' : dir === 'down' ? '▼' : ''} {item.change}
          </span>
        )}
        {item.sub && <span style={{ fontSize: 13, color: 'var(--fg-3)' }}>{item.sub}</span>}
      </div>
      {item.spark && item.spark.length > 1 && <MiniSparkline data={item.spark} color={chColor} />}
    </div>
  );
}

// A tiny trend line inside a KPI card. Reuses the shared area geometry with a
// compact viewport; colored to match the metric's direction. Web-only —
// Metrics has no Slack rasterizer.
function MiniSparkline({ data, color }: { data: number[]; color: string }) {
  const g = areaGeometry(data, undefined, { W: 140, H: 34, padL: 2, padR: 2, padT: 5, padB: 4 });
  const stroke = color === 'var(--fg-2)' ? 'var(--he-purple)' : color;
  return (
    <svg viewBox={`0 0 ${g.dims.W} ${g.dims.H}`} style={{ width: '100%', height: 34, display: 'block', marginTop: 2 }} preserveAspectRatio="none">
      <path d={g.areaPath} fill={stroke} opacity={0.1} />
      <path d={g.linePath} fill="none" stroke={stroke} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
export function MetricsGrid({ items }: ComponentProps['Metrics']) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 12,
      }}
    >
      {items.map((it, i) => (
        <Metric key={i} item={it} />
      ))}
    </div>
  );
}

// ---- card wrapper for charts/tables --------------------------------------
function Panel({ title, children, pad = 18 }: { title?: string; children: ReactNode; pad?: number }) {
  return (
    <div
      style={{
        background: 'var(--bg-1)',
        borderRadius: 18,
        padding: pad,
      }}
    >
      {title && (
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg-1)', marginBottom: 14 }}>
          {title}
        </div>
      )}
      {children}
    </div>
  );
}

// ---- Text + Heading blocks ----------------------------------------------
export function TextBlock({ text }: ComponentProps['Text']) {
  return <RichText text={text} style={{ margin: 0, fontSize: 15.5, lineHeight: 1.6, color: 'var(--fg-1)' }} />;
}
export function HeadingBlock({ text }: ComponentProps['Heading']) {
  return <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em' }}>{text}</div>;
}

/** Flip to `true` shortly after mount so charts can animate in from zero. */
function useGrowIn(delay = 60): boolean {
  const [grown, setGrown] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setGrown(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  return grown;
}

/**
 * Shared highlight state for charts. Emphasis is "details on demand": hover or
 * keyboard focus highlights transiently; a click/tap pins it so TOUCH devices
 * (which have no hover) still work. The shown item is the hovered/focused one,
 * falling back to the pinned one. Emphasis is by de-emphasis (dim the rest) —
 * no added chrome, no motion — and every value is already on screen, so this is
 * enhancement, never the only way to read the data.
 */
function useHighlight() {
  const [hovered, setHovered] = useState<number | null>(null);
  const [pinned, setPinned] = useState<number | null>(null);
  const shown = hovered ?? pinned;
  const pin = (i: number) => setPinned((p) => (p === i ? null : i));
  const bind = (i: number) => ({
    onMouseEnter: () => setHovered(i),
    onMouseLeave: () => setHovered(null),
    onFocus: () => setHovered(i),
    onBlur: () => setHovered(null),
    onClick: () => pin(i),
    // role="button" elements must respond to Enter/Space, so keyboard users can
    // pin the same way a tap does.
    onKeyDown: (e: ReactKeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        pin(i);
      }
    },
  });
  return { shown, bind };
}

// ---- Area / line chart ---------------------------------------------------
export function AreaChart({ title, data, unit = '', floor, xLabels, xLabel, yLabel }: ComponentProps['Area']) {
  const ref = useRef<SVGSVGElement>(null);
  const [hover, setHover] = useState<number | null>(null);
  const grown = useGrowIn(40);
  // Guard against degenerate series the live model could emit (empty / single / flat).
  if (data.length === 0) {
    return (
      <Panel title={title}>
        <div style={{ fontSize: 13, color: 'var(--fg-3)' }}>No data to chart.</div>
      </Panel>
    );
  }
  // Layout + path/coordinate math is shared with the Slack rasterizer — see catalog/src/svg/area.ts.
  const labels = areaLabelsOf({ data, xLabels, xLabel, yLabel });
  const dims = areaDims(labels);
  const g = areaGeometry(data, floor, dims);
  const { W, H, padL, padR, padT, padB } = dims;
  const { scaleX: x, scaleY: y, linePath: line, areaPath: area, yTicks } = g;
  const u = tickUnit(unit);
  const xTicks = xLabels && xLabels.length ? sparseXTicks(xLabels.length).filter((i) => i < data.length) : [];

  function onMove(e: ReactMouseEvent<SVGSVGElement>) {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * W;
    let i = Math.round(((px - padL) / (W - padL - padR)) * (data.length - 1));
    i = Math.max(0, Math.min(data.length - 1, i));
    setHover(i);
  }
  return (
    <Panel title={title}>
      <div style={{ position: 'relative' }}>
        <svg
          ref={ref}
          viewBox={`0 0 ${W} ${H}`}
          style={{ width: '100%', display: 'block', overflow: 'visible' }}
          onMouseMove={onMove}
          onMouseLeave={() => setHover(null)}
        >
          <defs>
            <linearGradient id="heArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--he-purple)" stopOpacity="0.22" />
              <stop offset="100%" stopColor="var(--he-purple)" stopOpacity="0" />
            </linearGradient>
          </defs>
          {yTicks.map((t, gi) => (
            <Fragment key={gi}>
              <line x1={padL} y1={t.y} x2={W - padR} y2={t.y} stroke="var(--border-1)" strokeWidth="1" />
              <text x={padL - 8} y={t.y + 4} textAnchor="end" fontSize="11" fill="var(--fg-3)">
                {formatTick(t.value)}
                {u}
              </text>
            </Fragment>
          ))}
          {xTicks.map((i) => (
            <text
              key={i}
              x={x(i)}
              y={H - (labels.xTitle ? 22 : 6)}
              textAnchor={xTickAnchor(i, xLabels?.length ?? 0)}
              fontSize="11"
              fill="var(--fg-3)"
            >
              {xLabels?.[i]}
            </text>
          ))}
          {xLabel && (
            <text x={(padL + (W - padR)) / 2} y={H - 4} textAnchor="middle" fontSize="11" fontWeight="600" fill="var(--fg-3)">
              {xLabel}
            </text>
          )}
          {yLabel && (
            <text x={14} y={H / 2} textAnchor="middle" fontSize="11" fontWeight="600" fill="var(--fg-3)" transform={`rotate(-90 14 ${H / 2})`}>
              {yLabel}
            </text>
          )}
          <path d={area} fill="url(#heArea)" style={{ opacity: grown ? 1 : 0, transition: 'opacity .6s ease' }} />
          <path
            d={line}
            fill="none"
            stroke="var(--he-purple)"
            strokeWidth="2.5"
            strokeLinejoin="round"
            strokeLinecap="round"
            style={{ strokeDasharray: 2600, strokeDashoffset: grown ? 0 : 2600, transition: 'stroke-dashoffset 1s ease' }}
          />
          {hover != null && (
            <g>
              <line
                x1={x(hover)}
                y1={padT}
                x2={x(hover)}
                y2={H - padB}
                stroke="var(--he-purple)"
                strokeWidth="1"
                strokeDasharray="3 3"
                opacity="0.5"
              />
              <circle cx={x(hover)} cy={y(data[hover])} r="4.5" fill="#fff" stroke="var(--he-purple)" strokeWidth="2.5" />
            </g>
          )}
        </svg>
        {hover != null && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: `${(x(hover) / W) * 100}%`,
              transform: 'translateX(-50%)',
              background: 'var(--he-gray-900)',
              color: '#fff',
              padding: '5px 9px',
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
            }}
          >
            {xLabels?.[hover] ?? `Day ${hover + 1}`}: {data[hover]}
            {unit}
          </div>
        )}
      </div>
    </Panel>
  );
}

// ---- Horizontal bar chart ------------------------------------------------
export function BarChart({ title, data, unit = '' }: ComponentProps['Bars']) {
  const grown = useGrowIn();
  const { shown, bind } = useHighlight();
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <Panel title={title}>
      {/* One shared grid so the label column sizes to the widest (full) name
          and rows stay aligned — Hotspot names are never truncated. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(80px, max-content) 1fr auto', alignItems: 'center', columnGap: 12, rowGap: 12 }}>
        {data.map((d, i) => {
          // Hover / focus / tap any cell in a row focuses that bar and dims the rest.
          const b = bind(i);
          const cell = { opacity: shown !== null && shown !== i ? 0.4 : 1, transition: 'opacity .15s ease' };
          return (
            <Fragment key={i}>
              <div {...b} style={{ ...cell, fontSize: 13, color: 'var(--fg-2)', fontWeight: 500, textAlign: 'right', whiteSpace: 'nowrap' }}>{d.label}</div>
              <div
                {...b}
                className="he-focusable"
                tabIndex={0}
                role="button"
                aria-label={`${d.label}: ${d.value.toLocaleString()}${unit ? ` ${unit}` : ''}`}
                style={{ ...cell, background: 'var(--bg-3)', borderRadius: 8, height: 22, overflow: 'hidden', cursor: 'default' }}
              >
                <div
                  style={{
                    width: grown ? `${barFraction(d.value, max)}%` : '0%',
                    height: '100%',
                    background: d.color || 'var(--he-purple)',
                    borderRadius: 8,
                    transition: `width .8s cubic-bezier(.22,1,.36,1) ${i * 60}ms`,
                  }}
                />
              </div>
              <div {...b} style={{ ...cell, fontSize: 13, fontWeight: 600, minWidth: 54, textAlign: 'right' }}>
                {d.value.toLocaleString()}
                <span style={{ color: 'var(--fg-3)', fontWeight: 500 }}>{unit && unit.length < 4 ? unit : ''}</span>
              </div>
            </Fragment>
          );
        })}
      </div>
    </Panel>
  );
}

// ---- Split (stacked) bar -------------------------------------------------
/** A single horizontal bar split into proportional segments (vote tally, share-of-total). */
export function SplitBar({ title, segments, unit = '' }: ComponentProps['Split']) {
  const grown = useGrowIn();
  const { shown, bind } = useHighlight();
  if (segments.length === 0) {
    return (
      <Panel title={title}>
        <div style={{ fontSize: 13, color: 'var(--fg-3)' }}>No data to chart.</div>
      </Panel>
    );
  }
  const { pieces, total } = splitPieces(segments);
  const colors = pieces.map((p, i) => splitColor(p.color, i));
  const dim = (i: number) => shown !== null && shown !== i;
  const legend = (p: (typeof pieces)[number]) =>
    `${p.label}: ${total > 0 ? `${p.pct}%` : '—'}, ${p.value.toLocaleString()}${unit ? ` ${unit}` : ''}`;
  return (
    <Panel title={title}>
      {/* The bar segment is the focusable/tappable control; the legend row below
          brushes the same index (hover/tap) but isn't a separate tab stop. */}
      <div style={{ display: 'flex', height: 26, borderRadius: 8, overflow: 'hidden', background: 'var(--bg-3)' }}>
        {pieces.map((p, i) => (
          <div
            key={i}
            {...bind(i)}
            className="he-focusable"
            tabIndex={0}
            role="button"
            aria-label={legend(p)}
            style={{
              width: grown ? `${p.fraction * 100}%` : '0%',
              height: '100%',
              background: colors[i],
              opacity: dim(i) ? 0.4 : 1,
              cursor: 'default',
              transition: `width .8s cubic-bezier(.22,1,.36,1) ${i * 60}ms, opacity .15s ease`,
            }}
          />
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 18px', marginTop: 14 }}>
        {pieces.map((p, i) => (
          <div
            key={i}
            {...bind(i)}
            style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, cursor: 'default', opacity: dim(i) ? 0.5 : 1, transition: 'opacity .15s ease' }}
          >
            <span style={{ width: 11, height: 11, borderRadius: 3, background: colors[i], flexShrink: 0 }} />
            <span style={{ color: shown === i ? 'var(--fg-1)' : 'var(--fg-2)', fontWeight: shown === i ? 700 : 500 }}>{p.label}</span>
            <span style={{ fontWeight: 700 }}>{total > 0 ? `${p.pct}%` : '—'}</span>
            <span style={{ color: 'var(--fg-3)' }}>
              {p.value.toLocaleString()}
              {unit ? ` ${unit}` : ''}
            </span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

// ---- Pie / donut chart ---------------------------------------------------
export function PieChart({ title, segments, unit = '', donut = false }: ComponentProps['Pie']) {
  const grown = useGrowIn();
  const geo = segments && segments.length ? pieGeometry(segments, donut) : null;
  if (!geo || geo.total <= 0) {
    return (
      <Panel title={title}>
        <div style={{ fontSize: 13, color: 'var(--fg-3)' }}>No data to chart.</div>
      </Panel>
    );
  }
  const { chartSize } = PIE_DIMS;
  const { shown, bind } = useHighlight();
  const dim = (i: number) => shown !== null && shown !== i;
  return (
    <Panel title={title}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
        <svg
          viewBox={`0 0 ${chartSize} ${chartSize}`}
          width={chartSize}
          height={chartSize}
          style={{ flexShrink: 0, maxWidth: '100%', opacity: grown ? 1 : 0, transform: grown ? 'scale(1)' : 'scale(.92)', transition: 'opacity .5s ease, transform .5s cubic-bezier(.22,1,.36,1)' }}
        >
          {geo.slices.map((s, i) => (
            <path
              key={i}
              d={s.path}
              fill={splitColor(s.color, s.colorIndex)}
              {...bind(i)}
              className="he-focusable"
              tabIndex={0}
              role="button"
              aria-label={`${s.label}: ${s.pct}%, ${s.value.toLocaleString()}${unit ? ` ${unit}` : ''}`}
              style={{ cursor: 'default', opacity: dim(i) ? 0.4 : 1, transition: 'opacity .15s ease' }}
            >
              <title>{`${s.label}: ${s.pct}%`}</title>
            </path>
          ))}
        </svg>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, minWidth: 150 }}>
          {geo.slices.map((s, i) => (
            <div
              key={i}
              {...bind(i)}
              style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'default', opacity: dim(i) ? 0.5 : 1, transition: 'opacity .15s ease' }}
            >
              <span style={{ width: 11, height: 11, borderRadius: 3, background: splitColor(s.color, s.colorIndex), flexShrink: 0 }} />
              <span style={{ color: shown === i ? 'var(--fg-1)' : 'var(--fg-2)', fontWeight: shown === i ? 700 : 500 }}>{s.label}</span>
              <span style={{ fontWeight: 700 }}>{s.pct}%</span>
              <span style={{ color: 'var(--fg-3)' }}>
                {s.value.toLocaleString()}
                {unit ? ` ${unit}` : ''}
              </span>
            </div>
          ))}
        </div>
      </div>
    </Panel>
  );
}

// ---- Radial gauge --------------------------------------------------------
export function RadialGauge({ title, value, max = 100, unit = '', label, color }: ComponentProps['Radial']) {
  const grown = useGrowIn();
  const g = radialGeometry(value, max);
  const { size, cx, cy, r, strokeWidth } = g.dims;
  const arcColor = color || 'var(--he-purple)';
  const circ = 2 * Math.PI * r;
  return (
    <Panel title={title}>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} style={{ maxWidth: '100%' }}>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--bg-3)" strokeWidth={strokeWidth} />
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={arcColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circ}
            strokeDashoffset={grown ? circ * (1 - g.fraction) : circ}
            transform={`rotate(-90 ${cx} ${cy})`}
            style={{ transition: 'stroke-dashoffset 1s cubic-bezier(.22,1,.36,1)' }}
          />
          <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fontSize={40} fontWeight={700} fill="var(--fg-1)">
            {Number.isFinite(value) ? value.toLocaleString() : '—'}
            {unit}
          </text>
          {label && (
            <text x={cx} y={cy + 30} textAnchor="middle" dominantBaseline="central" fontSize={14} fill="var(--fg-3)">
              {label}
            </text>
          )}
        </svg>
      </div>
    </Panel>
  );
}

// ---- Badge / status pills ------------------------------------------------
export function BadgePills({ items }: ComponentProps['Badge']) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {items.map((it, i) => {
        const token = SEMANTIC_TOKEN[it.tone ?? 'neutral'] ?? SEMANTIC_TOKEN.neutral;
        return (
          <span
            key={i}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 11px',
              borderRadius: 999,
              background: tint(token, 12),
              color: token,
              fontSize: 13,
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: 999, background: token }} />
            {it.text}
          </span>
        );
      })}
    </div>
  );
}

// ---- Data table ----------------------------------------------------------
/** Render one table cell: a link object as an anchor, a status column as a badge, else the scalar. */
function TableCell({ value, isStatus }: { value: string | number | { text: string; href: string }; isStatus: boolean }) {
  if (value !== null && typeof value === 'object') {
    return <InlineLink href={value.href}>{value.text}</InlineLink>;
  }
  if (isStatus) return <StatusBadge status={String(value)} />;
  return <>{value}</>;
}

export function DataTable({ title, columns, rows, status, align = [] }: ComponentProps['Table']) {
  return (
    <div
      style={{
        background: 'var(--bg-1)',
        borderRadius: 18,
        overflow: 'hidden',
      }}
    >
      {title && (
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg-1)', padding: '16px 18px 14px' }}>{title}</div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table className="he-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr>
              {columns.map((c, i) => (
                <th
                  key={i}
                  style={{
                    textAlign: align[i] === 'right' ? 'right' : 'left',
                    padding: '13px 18px',
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--fg-2)',
                    textTransform: 'uppercase',
                    letterSpacing: '.04em',
                    borderBottom: '1px solid var(--border-1)',
                    whiteSpace: 'nowrap',
                    background: 'var(--bg-2)',
                  }}
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr
                key={ri}
                style={{ transition: 'background .12s' }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-2)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                {row.map((cellValue, ci) => (
                  <td
                    key={ci}
                    style={{
                      textAlign: align[ci] === 'right' ? 'right' : 'left',
                      padding: '13px 18px',
                      borderBottom: ri === rows.length - 1 ? 'none' : '1px solid var(--border-1)',
                      color: ci === 0 ? 'var(--fg-1)' : 'var(--fg-2)',
                      fontWeight: ci === 0 ? 600 : 400,
                      fontVariantNumeric: align[ci] === 'right' ? 'tabular-nums' : 'normal',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <TableCell value={cellValue} isStatus={status === ci} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- Hotspot card --------------------------------------------------------
function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--fg-3)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 600 }}>{value}</div>
    </div>
  );
}
/**
 * Build the photo-card background, embedding `photo` only when it's a safe
 * http(s) URL. `photo` is model-controlled (untrusted), and React inline styles
 * are NOT sanitized — interpolating a raw value into `url(...)` would let a
 * crafted string break out of the token and inject arbitrary CSS (overlay /
 * UI-redress) or beacon out. Reuse the shared http(s) gate, then additionally
 * reject any character that could terminate the `url()` token; otherwise drop
 * the photo.
 */
function photoBackground(photo?: string): string {
  const base = 'var(--bg-3)';
  if (!photo) return base;
  const url = safeHttpUrl(photo);
  if (!url || /["'()\\\s]/.test(url.href)) return base;
  return `${base} url(${url.href}) center/cover`;
}

export function HotspotCard({ hotspot: h }: ComponentProps['Hotspot']) {
  const offline = h.status === 'offline';
  return (
    <div
      className="he-lift"
      style={{
        display: 'flex',
        gap: 0,
        background: 'var(--bg-1)',
        borderRadius: 18,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: 168,
          flexShrink: 0,
          background: photoBackground(h.photo),
          position: 'relative',
        }}
      >
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg,rgba(0,0,0,0) 60%,rgba(255,255,255,.04))' }} />
      </div>
      <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 10, flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em' }}>{h.name}</div>
            <div style={{ fontSize: 13, color: 'var(--fg-2)', marginTop: 2 }}>
              {[h.type, h.id ? `#${h.id}` : null, h.loc].filter(Boolean).join(' · ')}
            </div>
          </div>
          <StatusBadge status={h.status} />
        </div>
        <div style={{ display: 'flex', gap: 28, marginTop: 'auto', flexWrap: 'wrap' }}>
          <MiniStat label="Uptime" value={offline || h.uptime == null ? '—' : `${h.uptime}%`} />
          <MiniStat label="30d earned" value={offline || h.d30 == null ? '—' : `${h.d30.toLocaleString()} HNT`} />
          <MiniStat label="Clients" value={offline || h.clients == null ? '—' : h.clients.toLocaleString()} />
        </div>
      </div>
    </div>
  );
}

// ---- Callout -------------------------------------------------------------
const CALLOUT_TONE: Record<string, { bg: string; bd: string; fg: string }> = {
  purple: { bg: 'rgba(95,38,252,.06)', bd: 'rgba(95,38,252,.22)', fg: '#5F26FC' },
  orange: { bg: 'rgba(247,144,9,.07)', bd: 'rgba(247,144,9,.28)', fg: '#B45309' },
  blue: { bg: 'rgba(16,136,222,.07)', bd: 'rgba(16,136,222,.26)', fg: '#1088DE' },
};
export function Callout({ tone = 'purple', title, text }: ComponentProps['Callout']) {
  const t = CALLOUT_TONE[tone] || CALLOUT_TONE.purple;
  return (
    <div style={{ display: 'flex', gap: 12, background: t.bg, borderRadius: 16, padding: '14px 16px' }}>
      <Sparkle size={18} color={t.fg} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: t.fg, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '.04em' }}>
          {title}
        </div>
        <RichText text={text} style={{ margin: 0, fontSize: 14.5, lineHeight: 1.5, color: 'var(--fg-1)' }} />
      </div>
    </div>
  );
}

// ---- Follow-up chips -----------------------------------------------------
export function FollowUps({ items }: ComponentProps['Follow']) {
  const { onPrompt } = useChatActions();
  return (
    <div style={{ marginTop: 2 }}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--fg-3)',
          textTransform: 'uppercase',
          letterSpacing: '.05em',
          marginBottom: 8,
        }}
      >
        Suggested follow-ups
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {items.map((q, i) => (
          <button
            key={i}
            onClick={() => onPrompt(q)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              padding: '8px 14px',
              borderRadius: 999,
              border: '1px solid var(--border-1)',
              background: 'var(--bg-1)',
              color: 'var(--fg-1)',
              fontSize: 13.5,
              fontWeight: 500,
              fontFamily: 'inherit',
              cursor: 'pointer',
              transition: 'all .15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--he-purple)';
              e.currentTarget.style.color = 'var(--he-purple)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-1)';
              e.currentTarget.style.color = 'var(--fg-1)';
            }}
          >
            <span style={{ color: 'var(--he-purple)', fontSize: 15, lineHeight: 1 }}>+</span>
            {q}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---- Download card -------------------------------------------------------
/** Format a byte count as a short human string (null when unknown/invalid). */
function formatBytes(n?: number): string | null {
  if (n == null || !Number.isFinite(n) || n < 0) return null;
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

/**
 * Only http(s) URLs are safe to surface from agent-provided strings. Anything
 * not http/https (`javascript:`, `data:`, `vbscript:`, …) or empty is rejected.
 * This is the single URL-safety gate, shared by link hrefs ({@link safeHref})
 * and the Hotspot photo background ({@link photoBackground}), so the allowlist
 * lives in one place. Values are minted server-side, but the catalog schemas are
 * plain strings, so this is the defense-in-depth boundary at render time.
 */
function safeHttpUrl(value: string | undefined | null): URL | null {
  // Non-string guard, not just empty: a render part can hand a primitive props
  // of the wrong shape (e.g. a tool's streaming input before its output lands),
  // so `value` may be undefined at runtime despite the catalog type. Degrade to
  // "no link" rather than throwing and blanking the whole turn.
  if (typeof value !== 'string' || !value.trim()) return null;
  let u: URL;
  try {
    u = new URL(value, window.location.origin);
  } catch {
    return null;
  }
  return u.protocol === 'https:' || u.protocol === 'http:' ? u : null;
}

/** An http(s) href string for an anchor, or null when the value is unsafe. */
function safeHref(value: string): string | null {
  return safeHttpUrl(value)?.href ?? null;
}

export function DownloadCard({ url, filename, bytes, label }: ComponentProps['Download']) {
  const href = safeHref(url);
  // Title is the label (or filename); the second line carries the filename (only
  // when a label took the title), the size, and an "unavailable" marker when the
  // link didn't resolve. Omitted entirely when there's nothing to add.
  const subtitle = [label ? filename : null, formatBytes(bytes), href ? null : 'unavailable']
    .filter(Boolean)
    .join(' · ');

  const icon = (
    <div
      style={{
        width: 40,
        height: 40,
        flexShrink: 0,
        borderRadius: 10,
        background: 'rgba(95,38,252,.1)',
        color: 'var(--he-purple)',
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M14 3v4.5a1 1 0 0 0 1 1h4.5" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M12 11.5v5m0 0 2-2m-2 2-2-2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );

  const body = (
    <>
      {icon}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--fg-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label || filename}
        </div>
        {subtitle && (
          <div style={{ fontSize: 13, color: 'var(--fg-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {subtitle}
          </div>
        )}
      </div>
      {href && <span style={{ color: 'var(--he-purple)', fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap' }}>Download</span>}
    </>
  );

  const cardStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    background: 'var(--bg-1)',
    borderRadius: 16,
    padding: '14px 16px',
    textDecoration: 'none',
  };

  if (!href) {
    return (
      <div style={{ ...cardStyle, opacity: 0.7 }} title="This file is no longer available.">
        {body}
      </div>
    );
  }
  return (
    <a href={href} download={filename} target="_blank" rel="noopener noreferrer" style={cardStyle}>
      {body}
    </a>
  );
}

// The page id embedded in a share URL (/artifact/<uuid>) — used to read/set the
// page's visibility via the owner-only eve routes.
const PAGE_ID_RE = /\/artifact\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

/** A page id on its own, for validating one that arrived from a URL. */
export const PAGE_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The prompt "Edit with AI" sends when a page's refresh has broken. One
 * definition because both entry points (the in-chat card and the standalone
 * wrapper's `?edit=` deep link) must ask for the same thing: naming the
 * artifactId is what keeps the repair on the SAME page, so the refresh schedule,
 * the shared link and other people's pins don't stay on the broken original.
 */
export function pageRepairPrompt(pageId: string, url: string, title?: string): string {
  const named = title ? `\u201C${title}\u201D ` : '';
  return (
    `The interactive page ${named}at ${url} is failing to refresh its data. ` +
    `Please review it and fix the query or the page, then re-publish it with artifactId ${pageId} so it updates in place at the same link.`
  );
}
export function PageCard({ url, title }: ComponentProps['Page']) {
  const href = safeHref(url);
  // Like DownloadCard, but the deliverable is an interactive page to open in a
  // new tab, not a file to save — no `download` attr, and the affordance is
  // "Open page". Degrades to a dim, non-link card when the URL didn't resolve
  // (or a streaming part handed us the wrong shape) rather than throwing.
  const heading = title || 'Interactive page';
  const subtitle = href ? href.replace(/^https?:\/\//, '') : 'unavailable';
  const pageId = href ? (PAGE_ID_RE.exec(href)?.[1] ?? null) : null;


  // Owner-only refresh controls (see PageRefreshFooter). Independent of the
  // visibility gate above — both degrade to nothing for non-owners / static pages.
  const refresh = usePageRefresh(pageId);
  // Owner-only visibility control (private/domain/public), shared with the
  // artifact wrapper. Renders nothing for non-owners (degrade, never throw).
  const vis = usePageVisibility(pageId);
  const visibilityControl = <VisibilityControl state={vis} dropUpNearViewportBottom />;
  const { onPrompt } = useChatActions();
  // Names the artifactId, not just the link: a repair published as a NEW page
  // leaves the refresh schedule, the shared link and other people's pins on the
  // broken original, which is the opposite of a fix.
  // `pageId` is null only when the href carried no page id, in which case the
  // refresh footer that offers this never renders — so this is unreachable rather
  // than defended, and asking with no id would be worse than not asking.
  const editWithAI = () => {
    if (pageId) onPrompt(pageRepairPrompt(pageId, url, title || 'Interactive page'));
  };

  const icon = (
    <div
      style={{
        width: 40,
        height: 40,
        flexShrink: 0,
        borderRadius: 10,
        background: 'rgba(95,38,252,.1)',
        color: 'var(--he-purple)',
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
        <path d="M3 9h18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M6.5 6.5h.01M9 6.5h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    </div>
  );

  const info = (
    <>
      {icon}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--fg-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {heading}
        </div>
        <div style={{ fontSize: 13, color: 'var(--fg-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {subtitle}
        </div>
      </div>
    </>
  );

  const cardStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    background: 'var(--bg-1)',
    borderRadius: 16,
    padding: '14px 16px',
  };

  // Degraded: URL didn't resolve (purged page or a streaming part handed us the
  // wrong shape) — dim, non-interactive, no control.
  if (!href) {
    return (
      <div style={{ ...cardStyle, opacity: 0.7 }} title="This page is no longer available.">
        {info}
      </div>
    );
  }
  // The card is no longer a single <a> — the interactive visibility control
  // can't nest in a link — so the info + "Open page" affordance is the link and
  // the control is a sibling. The control renders only when the eve GET said this
  // viewer owns and may manage the page; otherwise the card looks exactly as before.
  return (
    <div style={{ ...cardStyle, flexDirection: 'column', alignItems: 'stretch', gap: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0, textDecoration: 'none', color: 'inherit' }}
        >
          {info}
          <span style={{ color: 'var(--he-purple)', fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap' }}>Open page</span>
        </a>
        {visibilityControl}
      </div>
      {refresh.state && (
        <PageRefreshFooter
          state={refresh.state}
          busy={refresh.busy}
          actionError={refresh.actionError}
          onRefresh={refresh.refreshNow}
          onSchedule={refresh.setSchedule}
          onEdit={editWithAI}
        />
      )}
    </div>
  );
}

// ---- Geographic map (Mapbox GL, lazy-loaded) -----------------------------
function Legend({ swatch, label }: { swatch: ReactNode; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {swatch}
      {label}
    </span>
  );
}

// Marker colors come from the catalog `mapPointColor`/`mapDisplayLegend` helpers
// (legend/group -> status -> cat-1 default), so the SPA, the Slack dot rasterizer,
// and the legend all agree. They return concrete hex (Mapbox paint + the swatches
// can't read CSS `var(--he-*)` tokens), so the per-point color is precomputed in
// JS and carried on each GeoJSON feature. The SPA passes the theme as the palette
// `mode` so categorical hues + the cat-1 default track the light/dark basemap;
// the reserved status hues stay fixed.
const MAP_HEIGHT = 380;
const MAP_STYLE = {
  light: 'mapbox://styles/mapbox/light-v11',
  dark: 'mapbox://styles/mapbox/dark-v11',
} as const;

/** Finite, in-range coordinates only — a NaN or out-of-range value would throw
 *  inside Mapbox's fitBounds/source, so drop those points rather than crash. */
function validMapPoints(points: MapPoint[] | undefined): MapPoint[] {
  if (!Array.isArray(points)) return [];
  return points.filter(
    (p) =>
      p != null &&
      Number.isFinite(p.lat) &&
      Number.isFinite(p.lon) &&
      Math.abs(p.lat) <= 90 &&
      Math.abs(p.lon) <= 180,
  );
}

/** Location-pin glyph for the venue row. A fixed constant (no user data), so
 *  assigning it via `innerHTML` is safe — nothing model-supplied is interpolated. */
const MAP_POPUP_PIN_SVG =
  '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="2.6"/></svg>';

/** A safe popup body built with DOM nodes: `textContent` (never innerHTML)
 *  auto-escapes, so a model-supplied label/sub/href can't inject markup (the one
 *  `innerHTML` is the static pin constant above). Hierarchy is name → venue (pin
 *  + `sub`) → a quiet category footer. `color` is the point's resolved marker
 *  color (theme-aware hex, shown as the category dot); `meta` is its
 *  category/status label. Chrome + typography are theme-aware via the
 *  `.he-mappop*` / `.mapboxgl-popup-*` rules in global.css. */
function mapPopupContent(p: MapPoint, color: string, meta: string | undefined): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'he-mappop';
  if (p.label) {
    const label = document.createElement('div');
    label.className = 'he-mappop-label';
    label.textContent = p.label;
    wrap.appendChild(label);
  }
  if (p.sub) {
    const venue = document.createElement('div');
    venue.className = 'he-mappop-venue';
    const pin = document.createElement('span');
    pin.className = 'he-mappop-pin';
    pin.innerHTML = MAP_POPUP_PIN_SVG; // static constant — no user data
    const text = document.createElement('span');
    text.textContent = p.sub;
    venue.append(pin, text);
    wrap.appendChild(venue);
  }
  if (meta) {
    const cat = document.createElement('div');
    cat.className = 'he-mappop-cat';
    const dot = document.createElement('span');
    dot.className = 'he-mappop-dot';
    dot.style.background = color;
    const text = document.createElement('span');
    text.textContent = meta;
    cat.append(dot, text);
    wrap.appendChild(cat);
  }
  const href = p.href ? safeHref(p.href) : null;
  if (href) {
    const a = document.createElement('a');
    a.className = 'he-mappop-link';
    a.href = href;
    a.textContent = 'Details';
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    wrap.appendChild(a);
  }
  return wrap;
}

/** Add (or re-add, after a `setStyle`) the point source + status-colored circle
 *  layer. Every popup field rides in the feature `properties` — those survive
 *  Mapbox's GeoJSON tiling, whereas the geometry coordinates are quantized to
 *  tile space and can't be matched back to the source degrees, so the click
 *  handler reads properties rather than recovering the point by coordinate. */
function addPointsLayer(
  m: MapboxMap,
  pts: MapPoint[],
  legend: ComponentProps['Map']['legend'],
  mode: 'light' | 'dark',
): void {
  if (m.getSource('points')) return;
  m.addSource('points', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: pts.map((p) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [p.lon, p.lat] },
        // `color` is precomputed here (legend/group -> status -> cat-1 default) so
        // the paint expression is a plain `['get', 'color']` and never drifts from
        // the catalog helper the legend + Slack renderer also use.
        properties: {
          label: p.label ?? '',
          status: p.status ?? '',
          group: p.group ?? '',
          sub: p.sub ?? '',
          href: p.href ?? '',
          color: mapPointColor(p, legend, mode),
        },
      })),
    },
  });
  m.addLayer({
    id: 'points',
    type: 'circle',
    source: 'points',
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 6, 4, 12, 7],
      'circle-color': ['get', 'color'],
      'circle-stroke-width': 1.5,
      'circle-stroke-color': '#ffffff',
      'circle-opacity': 0.9,
    },
  });
}

/** A centered message occupying the map's footprint — used before Mapbox is
 *  ready and for every degrade path (no token, no points, load failure). */
function MapPlaceholder({ text }: { text: string }) {
  return (
    <div
      style={{
        height: MAP_HEIGHT,
        borderRadius: 12,
        background: 'var(--bg-2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--fg-3)',
        fontSize: 13.5,
        textAlign: 'center',
        padding: 16,
      }}
    >
      {text}
    </div>
  );
}

/**
 * Interactive geographic map. Mapbox GL is loaded lazily (a dynamic import), so
 * its ~800 KB stays out of the main bundle and only downloads when a map block
 * actually renders. Points become a GeoJSON source drawn as a circle layer whose
 * fill follows the catalog precedence (legend/group -> status -> cat-1 default),
 * in the current theme's palette step; clicking a marker opens a popup. The
 * viewport auto-fits the points unless
 * `center`/`zoom` are given.
 *
 * A "Loading map…" overlay covers the map until Mapbox's first `idle`, so a slow
 * cold load (the lazy chunk + tiles) doesn't read as a finished, empty map.
 *
 * Degrades, never throws (a throw here blanks the whole turn): a missing token,
 * empty/invalid points, or a Mapbox load failure all fall back to a placeholder.
 */
export function MapBlock({ title, points, center, zoom, legend }: ComponentProps['Map']) {
  const [theme] = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapboxMap | null>(null);
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(false);
  // Runtime-minted Mapbox token (see ../lib/mapboxToken), fetched once per mount.
  // `undefined` = still loading, `null` = unavailable (no token from the Worker
  // and no dev/gallery fallback), a string = ready to use.
  const [token, setToken] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    getMapboxToken().then((t) => {
      if (alive) setToken(t ?? null);
    });
    return () => {
      alive = false;
    };
  }, []);

  const pts = validMapPoints(points);
  const display = mapDisplayLegend({ points: pts, legend }, theme);
  const groupLabel = new Map(display.map((e) => [e.key, e.label]));
  const legendMode = !!(legend && legend.length > 0);
  // Honor an explicit center only when it's finite + in range; otherwise ignore
  // it and auto-fit the points, so a bad center can't fail an otherwise-valid map.
  const useCenter =
    center &&
    Number.isFinite(center.lat) &&
    Number.isFinite(center.lon) &&
    Math.abs(center.lat) <= 90 &&
    Math.abs(center.lon) <= 180
      ? center
      : null;
  // Value key: the map is (re)built only when the plotted data or viewport
  // actually change, not on every parent re-render (renderBlock hands us a fresh
  // props object each time). It covers ALL displayed fields (via JSON) so a
  // label/sub/href/group/legend edit refreshes the markers + popups. Theme is
  // deliberately excluded — a light/dark toggle restyles in place (below),
  // keeping the user's pan/zoom.
  const sig =
    JSON.stringify(pts) +
    `|${useCenter?.lat ?? ''},${useCenter?.lon ?? ''}|${zoom ?? ''}` +
    `|${JSON.stringify(legend ?? [])}`;
  const hasToken = !!token;
  const renderable = hasToken && pts.length > 0 && !failed;

  // Build the map, and rebuild it when the data/viewport signature changes.
  useEffect(() => {
    if (!renderable || !containerRef.current) return;
    let cancelled = false;
    setReady(false);
    (async () => {
      try {
        const mapboxgl = (await import('mapbox-gl')).default;
        await import('mapbox-gl/dist/mapbox-gl.css');
        if (cancelled || !containerRef.current) return;
        mapboxgl.accessToken = token as string; // non-empty whenever `renderable` is true
        const m = new mapboxgl.Map({
          container: containerRef.current,
          style: MAP_STYLE[theme],
          center: useCenter ? [useCenter.lon, useCenter.lat] : [pts[0].lon, pts[0].lat],
          zoom: zoom ?? 9,
          attributionControl: true,
        });
        mapRef.current = m;
        m.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
        const popup = new mapboxgl.Popup({ closeButton: true, offset: 12, maxWidth: '238px' });

        // First `idle` = basemap + tiles + markers painted; drop the loading overlay.
        m.once('idle', () => { if (!cancelled) setReady(true); });

        m.on('load', () => {
          if (cancelled) return;
          addPointsLayer(m, pts, legend, theme);
          // Fit to the points unless an explicit viewport was given. maxZoom keeps
          // a single point (zero-area bounds) from zooming to street level.
          if (!useCenter && pts.length > 1) {
            const bounds = new mapboxgl.LngLatBounds();
            pts.forEach((p) => bounds.extend([p.lon, p.lat]));
            m.fitBounds(bounds, { padding: 44, maxZoom: 14, duration: 0 });
          }
        });

        // Popup fields come from the clicked feature's `properties` (reliable),
        // not from matching its quantized coordinates back to a source point. The
        // marker color rides in `properties.color` (precomputed via mapPointColor).
        m.on('click', 'points', (e) => {
          const f = e.features?.[0];
          if (!f || f.geometry.type !== 'Point') return;
          const [lon, lat] = f.geometry.coordinates as [number, number];
          const pr = (f.properties ?? {}) as Record<string, string>;
          const meta = legendMode
            ? pr.group
              ? groupLabel.get(pr.group) ?? pr.group
              : undefined
            : pr.status || undefined;
          popup
            .setLngLat([lon, lat])
            .setDOMContent(
              mapPopupContent(
                {
                  lat,
                  lon,
                  label: pr.label || undefined,
                  status: (pr.status as MapPoint['status']) || undefined,
                  group: pr.group || undefined,
                  sub: pr.sub || undefined,
                  href: pr.href || undefined,
                },
                pr.color || categoricalColor(0, theme),
                meta,
              ),
            )
            .addTo(m);
        });
        m.on('mouseenter', 'points', () => { m.getCanvas().style.cursor = 'pointer'; });
        m.on('mouseleave', 'points', () => { m.getCanvas().style.cursor = ''; });
        // A tile/style error after construction may mean `idle` never fires, so
        // also drop the loading overlay here — otherwise it sticks forever. The
        // map itself stays (partial tiles beat a blank placeholder).
        m.on('error', (e) => {
          console.error('mapbox error', e?.error ?? e);
          if (!cancelled) setReady(true);
        });
      } catch (err) {
        console.error('map render failed', err);
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      try {
        mapRef.current?.remove();
      } catch {
        /* map already torn down */
      }
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, renderable]);

  // Theme toggle: restyle the existing map in place and re-add the data layer once
  // the new basemap loads — a full rebuild would reset the user's pan/zoom. No-op
  // until the async build above has created the map (mapRef still null on mount).
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    m.setStyle(MAP_STYLE[theme]);
    m.once('style.load', () => addPointsLayer(m, pts, legend, theme));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  return (
    <Panel title={title}>
      {token === undefined ? (
        <MapPlaceholder text="Loading map…" />
      ) : !hasToken ? (
        <MapPlaceholder text="Map unavailable — no Mapbox token configured." />
      ) : pts.length === 0 ? (
        <MapPlaceholder text="No mappable locations." />
      ) : failed ? (
        <MapPlaceholder text="Couldn’t load the map." />
      ) : (
        <div style={{ position: 'relative', height: MAP_HEIGHT }}>
          <div ref={containerRef} style={{ height: MAP_HEIGHT, borderRadius: 12, overflow: 'hidden' }} />
          {!ready && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: 12,
                background: 'var(--bg-2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--fg-3)',
                fontSize: 13.5,
              }}
            >
              Loading map…
            </div>
          )}
        </div>
      )}
      {renderable && (
        <div style={{ display: 'flex', gap: 18, marginTop: 12, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--fg-2)' }}>
          {display.map((e) => (
            <Legend
              key={e.key}
              swatch={<span style={{ width: 11, height: 11, borderRadius: 999, background: e.color, display: 'inline-block' }} />}
              label={e.label}
            />
          ))}
          <Legend swatch={<span />} label={`${pts.length} location${pts.length === 1 ? '' : 's'}`} />
        </div>
      )}
    </Panel>
  );
}

// ---- block dispatch (shared by EveAssistant + container children) ---------
/**
 * Render one catalog block by its `kind`. Shared by `<EveAssistant>`'s tool
 * dispatch and by the container primitives (Section/Tabs/Accordion) for their
 * child blocks. Unknown kinds degrade to null rather than throwing (a throw here
 * would blank the whole turn).
 */
export function renderBlock(kind: string, props: unknown, key?: string | number): ReactNode {
  const p = props as never;
  switch (kind) {
    case 'text':
      return <TextBlock key={key} {...(p as ComponentProps['Text'])} />;
    case 'heading':
      return <HeadingBlock key={key} {...(p as ComponentProps['Heading'])} />;
    case 'metrics':
      return <MetricsGrid key={key} {...(p as ComponentProps['Metrics'])} />;
    case 'area':
      return <AreaChart key={key} {...(p as ComponentProps['Area'])} />;
    case 'bars':
      return <BarChart key={key} {...(p as ComponentProps['Bars'])} />;
    case 'split':
      return <SplitBar key={key} {...(p as ComponentProps['Split'])} />;
    case 'pie':
      return <PieChart key={key} {...(p as ComponentProps['Pie'])} />;
    case 'radial':
      return <RadialGauge key={key} {...(p as ComponentProps['Radial'])} />;
    case 'table':
      return <DataTable key={key} {...(p as ComponentProps['Table'])} />;
    case 'hotspot':
      return <HotspotCard key={key} {...(p as ComponentProps['Hotspot'])} />;
    case 'map':
      return <MapBlock key={key} {...(p as ComponentProps['Map'])} />;
    case 'callout':
      return <Callout key={key} {...(p as ComponentProps['Callout'])} />;
    case 'badge':
      return <BadgePills key={key} {...(p as ComponentProps['Badge'])} />;
    case 'follow':
      return <FollowUps key={key} {...(p as ComponentProps['Follow'])} />;
    case 'download':
      return <DownloadCard key={key} {...(p as ComponentProps['Download'])} />;
    case 'page':
      return <PageCard key={key} {...(p as ComponentProps['Page'])} />;
    case 'section':
      return <SectionLayout key={key} {...(p as ComponentProps['Section'])} />;
    case 'tabs':
      return <TabsBlock key={key} {...(p as ComponentProps['Tabs'])} />;
    case 'accordion':
      return <AccordionBlock key={key} {...(p as ComponentProps['Accordion'])} />;
    default:
      return null;
  }
}

/** Render an array of container child blocks (each carries its own `kind`). */
function renderChildren(blocks: ChildBlock[] | undefined): ReactNode {
  return (blocks ?? []).map((b, i) => renderBlock(b.kind, b, i));
}

// ---- Section (comparison / grouping layout) ------------------------------
export function SectionLayout({ title, columns = 1, blocks }: ComponentProps['Section']) {
  const cols = columns === 3 ? 3 : columns === 2 ? 2 : 1;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {title && <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em' }}>{title}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: 16, alignItems: 'start' }}>
        {(blocks ?? []).map((b, i) => (
          <div key={i} style={{ minWidth: 0 }}>
            {renderBlock(b.kind, b, i)}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Tabs ----------------------------------------------------------------
export function TabsBlock({ tabs }: ComponentProps['Tabs']) {
  const [active, setActive] = useState(0);
  if (!tabs || tabs.length === 0) return null;
  const idx = Math.min(active, tabs.length - 1);
  return (
    <div>
      <div role="tablist" style={{ display: 'flex', gap: 4, flexWrap: 'wrap', borderBottom: '1px solid var(--border-1)', marginBottom: 16 }}>
        {tabs.map((t, i) => (
          <button
            key={i}
            role="tab"
            aria-selected={i === idx}
            onClick={() => setActive(i)}
            style={{
              appearance: 'none',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px 12px',
              marginBottom: -1,
              fontSize: 14,
              fontWeight: 600,
              color: i === idx ? 'var(--fg-1)' : 'var(--fg-3)',
              borderBottom: i === idx ? '2px solid var(--he-purple)' : '2px solid transparent',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div role="tabpanel" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {renderChildren(tabs[idx]?.blocks)}
      </div>
    </div>
  );
}

// ---- Accordion -----------------------------------------------------------
export function AccordionBlock({ sections }: ComponentProps['Accordion']) {
  if (!sections || sections.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {sections.map((s, i) => (
        <details key={i} open={s.open} style={{ background: 'var(--bg-1)', borderRadius: 12, padding: '0 16px' }}>
          <summary style={{ cursor: 'pointer', padding: '14px 2px', fontSize: 14, fontWeight: 600 }}>{s.label}</summary>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '2px 0 16px' }}>{renderChildren(s.blocks)}</div>
        </details>
      ))}
    </div>
  );
}
